import { defaultTheme } from '../../HubLayout/styles/defaultTheme';
import { darkTheme } from '../../HubLayout/styles/darkTheme';
import { Theme } from '@fluentui/react';

//set theme preference based on localStorage
export const setThemeInLocalStorage = (theme: string) => {
    localStorage.setItem('themePreference', theme);
};

//get theme from localStorage
export const getThemeFromLocalStorage = (): 'dark' | 'default' => {
    const theme = localStorage.getItem('themePreference');
    if (theme && theme === 'dark') {
        return 'dark';
    }
    return 'default';
};

//get theme from string
export const getThemeFromString = (themeName: string | undefined): Theme => {
    return themeName
        ? themeName === 'dark'
            ? darkTheme
            : defaultTheme
        : defaultTheme;
};

// set the auto-refresh interval (in minutes, 0 = off) in localStorage
export const setAutoRefreshIntervalInLocalStorage = (minutes: number) => {
    localStorage.setItem('autoRefreshIntervalMinutes', String(minutes));
};

// get the auto-refresh interval (in minutes, 0 = off) from localStorage.
// Defaults to 10 minutes when the user has not made an explicit choice yet.
export const getAutoRefreshIntervalFromLocalStorage = (): number => {
    const value = localStorage.getItem('autoRefreshIntervalMinutes');
    if (value === null) {
        return 10;
    }

    const parsed = Number(value);
    const supportedIntervals = new Set([0, 1, 5, 10]);

    return Number.isInteger(parsed) && supportedIntervals.has(parsed)
        ? parsed
        : 10;
};

// Remembers which build/deployment status flyout was open on the Release
// Details page so it can be reopened after an auto-refresh page reload. Stored
// in sessionStorage so it is scoped to the tab and cleared when the tab closes.
const OPEN_STATUS_FLYOUT_KEY = 'openStatusFlyout';

export const setOpenStatusFlyoutInSessionStorage = (
    releaseCorrelationId: string,
    uniqueIdentifier: string
) => {
    sessionStorage.setItem(
        OPEN_STATUS_FLYOUT_KEY,
        JSON.stringify({ releaseCorrelationId, uniqueIdentifier })
    );
};

export const clearOpenStatusFlyoutInSessionStorage = () => {
    sessionStorage.removeItem(OPEN_STATUS_FLYOUT_KEY);
};

export const getOpenStatusFlyoutFromSessionStorage = ():
    | { releaseCorrelationId: string; uniqueIdentifier: string }
    | undefined => {
    const value = sessionStorage.getItem(OPEN_STATUS_FLYOUT_KEY);
    if (!value) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(value);
        if (parsed?.releaseCorrelationId && parsed?.uniqueIdentifier) {
            return parsed;
        }
    } catch {
        // Ignore malformed values and treat as no saved flyout.
    }
    return undefined;
};

// set search value in localStorage
export const setMultiStageReleaseSearchValueInLocalStorage = (
    searchValue: string
) => {
    localStorage.setItem('multiStageReleaseSearchValue', searchValue);
};

// set search value in localStorage
export const setMultiStageBuildSearchValueInLocalStorage = (
    searchValue: string
) => {
    localStorage.setItem('multiStageBuildSearchValue', searchValue);
};

// set start date in localStorage
export const setStartDateInLocalStorage = (searchValue: string) => {
    localStorage.setItem('startDate', searchValue);
};

// set end date in localStorage
export const setEndDateInLocalStorage = (searchValue: string) => {
    localStorage.setItem('endDate', searchValue);
};

// get start date value in localStorage
export const getStartDateInLocalStorage = () => {
    const searchValue = localStorage.getItem('startDate');
    return searchValue ? searchValue : undefined;
};

// get end date value in localStorage
export const getEndDateInLocalStorage = () => {
    const searchValue = localStorage.getItem('endDate');
    return searchValue ? searchValue : undefined;
};

// get search value in localStorage
export const getMultiStageReleaseSearchValueFromLocalStorage = () => {
    const searchValue = localStorage.getItem('multiStageReleaseSearchValue');
    return searchValue ? searchValue : undefined;
};

// get search value in localStorage
export const getMultiStageBuildSearchValueFromLocalStorage = () => {
    const searchValue = localStorage.getItem('multiStageBuildSearchValue');
    return searchValue ? searchValue : undefined;
};

// set selected service names in localStorage
export const setSelectedServiceNamesInLocalStorage = (
    selectedKeys: string[]
) => {
    localStorage.setItem('selectedServiceNames', JSON.stringify(selectedKeys));
};

// get selected service names in localStorage
export const getSelectedServiceNamesFromLocalStorage = () => {
    const selectedServiceNames = localStorage.getItem('selectedServiceNames');

    if (selectedServiceNames) {
        const parsedServiceNames = JSON.parse(selectedServiceNames);
        return parsedServiceNames as string[];
    }
};

// set selected project names in localStorage
export const setSelectedProjectNamesInLocalStorage = (
    selectedKeys: string[]
) => {
    localStorage.setItem('selectedProjectNames', JSON.stringify(selectedKeys));
};

// get selected project names in localStorage
export const getSelectedProjectNamesFromLocalStorage = () => {
    const selectedProjectNames = localStorage.getItem('selectedProjectNames');

    if (selectedProjectNames) {
        const parsedprojectNames = JSON.parse(selectedProjectNames);
        return parsedprojectNames as string[];
    }
};

// set selected enivornment names in localStorage
export const setSelectedEnvironmentNamesInLocalStorage = (
    selectedKeys: string[]
) => {
    localStorage.setItem(
        'selectedEnvironmentNames',
        JSON.stringify(selectedKeys)
    );
};

// set selected deployment types in localStorage
export const setSelectedDeploymentTypesInLocalStorage = (
    selectedKeys: string[]
) => {
    localStorage.setItem(
        'selectedDeploymentTypes',
        JSON.stringify(selectedKeys)
    );
};

// get selected environment names in localStorage
export const getSelectedEnvironmentNamesFromLocalStorage = () => {
    const selectedEnvironmentNames = localStorage.getItem(
        'selectedEnvironmentNames'
    );

    if (selectedEnvironmentNames) {
        const parsedEnvironmentNames = JSON.parse(selectedEnvironmentNames);
        return parsedEnvironmentNames as string[];
    }
};

// get selected deployment types in localStorage
export const getSelectedDeploymentTypesFromLocalStorage = () => {
    const selectedDeploymentTypes = localStorage.getItem(
        'selectedDeploymentTypes'
    );

    if (selectedDeploymentTypes) {
        const parsedDeploymentTypes = JSON.parse(selectedDeploymentTypes);
        return parsedDeploymentTypes as string[];
    }
};

export const clearAllSelectedFields = () => {
    setSelectedEnvironmentNamesInLocalStorage([]);
    setStartDateInLocalStorage('');
    setEndDateInLocalStorage('');
    setSelectedDeploymentTypesInLocalStorage([]);
    setSelectedProjectNamesInLocalStorage([]);
    setSelectedServiceNamesInLocalStorage([]);
    setMultiStageBuildSearchValueInLocalStorage('');
    setMultiStageReleaseSearchValueInLocalStorage('');
};
